import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Input from 'components/shared/form/Input';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/flexible/Profile';
import {
  AgentSettings,
  INSTRUCTIONS_LIMIT,
  MappingTarget,
  MappingType,
  NewHospitalSettings as NewHospitalSettingsType,
  THRESHOLD_MAX,
  THRESHOLD_MIN,
} from 'services/api/admin/mappings';
import { NEW_HOSPITALS_PATH, useCanReviewNewHospitals } from 'services/api/admin/newHospitals';
import { colors } from 'styles/theme/colors';
import ChevronDown from 'svg/ChevronDown';

import { Chip, Muted } from './Chips';
import OperationProblem from './OperationProblem';
import { PendingOperation, validThreshold } from './operations';

type InstructionsProps = {
  title: string;
  stored: string | null;
  pending: string | null;
  problem: PendingOperation | undefined;
  disabled: boolean;
  onChange: (text: string | null) => void;
  onUndo: (slot: string) => void;
};

export function AgentInstructions({ title, stored, pending, problem, disabled, onChange, onUndo }: InstructionsProps) {
  const [open, setOpen] = useState(pending !== null);
  const text = pending ?? stored ?? '';
  const over = text.length > INSTRUCTIONS_LIMIT;

  return (
    <Section>
      <SectionHeader onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>
          Agent instructions for automatic {title.toLowerCase()}{' '}
          {pending !== null && <Chip $tone='amber'>Pending</Chip>}
        </span>
        <Chevron $open={open} color={colors.black50} />
      </SectionHeader>
      {open && (
        <SectionBody>
          <Textarea
            value={text}
            disabled={disabled}
            rows={4}
            placeholder="Tell the agent anything specific to this client, e.g. 'SL means Sugar Land.'"
            onChange={(e) => onChange(e.target.value === (stored ?? '') ? null : e.target.value)}
          />
          <Counter $over={over}>
            {text.length.toLocaleString()} / {INSTRUCTIONS_LIMIT.toLocaleString()} · saves with the configuration
          </Counter>
        </SectionBody>
      )}
      <OperationProblem op={problem} onUndo={onUndo} />
    </Section>
  );
}

type FallbackProps = {
  stored: MappingTarget | null;
  pending: string[] | null | undefined;
  problem: PendingOperation | undefined;
  disabled: boolean;
  onChange: (target: string[] | null | undefined) => void;
  onKeepMine: (op: PendingOperation) => void;
  onUndo: (slot: string) => void;
};

function lines(target: MappingTarget | null | undefined) {
  if (!target) return [];
  return Array.isArray(target) ? target : [target];
}

export function FallbackRule({ stored, pending, problem, disabled, onChange, onKeepMine, onUndo }: FallbackProps) {
  const storedLines = lines(stored);
  const [draft, setDraft] = useState((pending === undefined ? storedLines : lines(pending)).join('\n'));

  const update = (value: string) => {
    setDraft(value);
    const next = value
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    const same = next.length === storedLines.length && next.every((l, i) => l === storedLines[i]);
    onChange(same ? undefined : next.length ? next : null);
  };

  return (
    <Section>
      <SectionTitle>Wildcard mappings {pending !== undefined && <Chip $tone='amber'>Pending</Chip>}</SectionTitle>
      <Muted>Tried for any value with no mapping, in order. One pattern per line; %{'{value}'} is the raw value.</Muted>
      <Textarea value={draft} disabled={disabled} rows={2} onChange={(e) => update(e.target.value)} />
      <OperationProblem op={problem} onKeepMine={onKeepMine} onUndo={onUndo} />
    </Section>
  );
}

const FALLBACK_AUTO_FLAGS: Partial<Record<MappingType, string>> = {
  groups: 'mapping_agent_auto_hospitals',
  plan_types: 'mapping_agent_auto_plan_types',
  group_types: 'mapping_agent_auto_group_types',
};

type AgentProps = {
  type: MappingType;
  stored: AgentSettings;
  pending: number | null;
  disabled: boolean;
  onChange: (threshold: number | null) => void;
};

export function AgentModeSettings({ type, stored, pending, disabled, onChange }: AgentProps) {
  const { profile } = useProfile();
  const canViewFlags = profile.has(PERMISSIONS.adminFeatureFlagView);
  const [threshold, setThreshold] = useState(String(pending ?? stored.threshold));
  const autoFlag = 'autoFlag' in stored ? stored.autoFlag : FALLBACK_AUTO_FLAGS[type];

  if (!autoFlag) return null;

  if (!stored.enabled) {
    return (
      <Section>
        <SectionTitle>Mapping agent</SectionTitle>
        <Muted>
          Agent is off for this client.{' '}
          {canViewFlags && (
            <Link to='/admin/feature-flags/mapping_agent/edit?tab=client' target='_blank' rel='noreferrer'>
              Turn it on in Feature Flags
            </Link>
          )}
        </Muted>
      </Section>
    );
  }

  const thresholdValid = threshold !== '' && validThreshold(Number(threshold));
  const update = (raw: string) => {
    setThreshold(raw);
    const value = raw === '' ? NaN : Number(raw);
    onChange(value === stored.threshold ? null : value);
  };

  return (
    <Section>
      <SectionTitle>Mapping agent {pending !== null && <Chip $tone='amber'>Pending</Chip>}</SectionTitle>
      <ModeRow>
        <Chip $tone={stored.mode === 'auto' ? 'green' : 'neutral'}>
          {stored.mode === 'auto' ? 'Auto: on by feature flag' : 'Suggest'}
        </Chip>
        {canViewFlags && (
          <Link to={`/admin/feature-flags/${autoFlag}/edit?tab=client`} target='_blank' rel='noreferrer'>
            Change in Feature Flags
          </Link>
        )}
      </ModeRow>
      <Muted>
        {stored.mode === 'auto'
          ? `Picks at or above the threshold are applied, and still show here for acknowledgement. Others are suggested.${
              type === 'groups'
                ? " Hospitals outside the client's network are always suggested, whatever the confidence."
                : ''
            }`
          : 'Every pick is left as a suggestion for review.'}
      </Muted>
      <ThresholdInput>
        <Input
          type='number'
          min={THRESHOLD_MIN}
          max={THRESHOLD_MAX}
          step={0.01}
          value={threshold}
          disabled={disabled}
          onChange={(e) => update(e.target.value)}
        />
        <Muted>confidence threshold (0.5 to 0.99)</Muted>
      </ThresholdInput>
      {!thresholdValid && <Counter $over>Enter a threshold between 0.5 and 0.99.</Counter>}
    </Section>
  );
}

type NewHospitalProps = {
  stored: NewHospitalSettingsType;
  agentEnabled: boolean;
  pending: number | null;
  disabled: boolean;
  onChange: (threshold: number | null) => void;
};

export function NewHospitalSettings({ stored, agentEnabled, pending, disabled, onChange }: NewHospitalProps) {
  const { profile } = useProfile();
  const canViewFlags = profile.has(PERMISSIONS.adminFeatureFlagView);
  const canReview = useCanReviewNewHospitals();
  const [threshold, setThreshold] = useState(String(pending ?? stored.threshold));
  const thresholdValid = threshold !== '' && validThreshold(Number(threshold));

  const update = (raw: string) => {
    setThreshold(raw);
    const value = raw === '' ? NaN : Number(raw);
    onChange(value === stored.threshold ? null : value);
  };

  if (!agentEnabled) {
    return (
      <Section>
        <SectionTitle>New hospitals</SectionTitle>
        <Muted>Inactive while the mapping agent is off for this client.</Muted>
      </Section>
    );
  }

  return (
    <Section>
      <SectionTitle>New hospitals {pending !== null && <Chip $tone='amber'>Pending</Chip>}</SectionTitle>
      <ModeRow>
        <Chip $tone={stored.auto ? 'green' : 'neutral'}>
          {stored.auto ? 'Auto-create: on by feature flag' : 'Review every draft'}
        </Chip>
        {canViewFlags && (
          <Link to={`/admin/feature-flags/${stored.autoFlag}/edit?tab=client`} target='_blank' rel='noreferrer'>
            Change in Feature Flags
          </Link>
        )}
      </ModeRow>
      <Muted>
        {stored.auto
          ? 'Ready drafts at or above the threshold are created without review. Others wait on the New Hospitals page.'
          : 'Every draft waits for review on the New Hospitals page.'}{' '}
        {canReview && (
          <Link to={NEW_HOSPITALS_PATH} target='_blank' rel='noreferrer'>
            Open New Hospitals
          </Link>
        )}
      </Muted>
      <ThresholdInput>
        <Input
          type='number'
          min={THRESHOLD_MIN}
          max={THRESHOLD_MAX}
          step={0.01}
          value={threshold}
          disabled={disabled}
          aria-label='New hospital confidence threshold'
          onChange={(e) => update(e.target.value)}
        />
        <Muted>confidence threshold (0.5 to 0.99)</Muted>
      </ThresholdInput>
      {!thresholdValid && <Counter $over>Enter a threshold between 0.5 and 0.99.</Counter>}
    </Section>
  );
}

const ModeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const ThresholdInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  input {
    width: 90px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Chevron = styled(ChevronDown)<{ $open: boolean }>`
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '180deg')});
  transform-origin: center;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.blue100};
  }
`;

const Counter = styled.span<{ $over: boolean }>`
  align-self: flex-end;
  font-size: 12px;
  color: ${({ $over }) => ($over ? colors.red100 : colors.black50)};
`;
